/**
 * ARMITA Color Utilities
 * 
 * Central file for color definitions and conversions used throughout the application
 */

// Map of vertebra labels to their corresponding colors in a vibrant fire-ice theme
export const vertebraColors = {
  // Vibrant fire (reds) and ice (blues) theme with bright colors throughout
  
  // Blue → Cyan (smooth interpolation by increasing green)
  1:  '#0000ff',  // (0,  0,   255) – Pure blue
  2:  '#0020ff',  // (0,  32,  255)
  3:  '#0040ff',  // (0,  64,  255)
  4:  '#0060ff',  // (0,  96,  255)
  5:  '#0080ff',  // (0,  128, 255)
  6:  '#00a0ff',  // (0,  160, 255)
  7:  '#00c0ff',  // (0,  192, 255)
  8:  '#00e0ff',  // (0,  224, 255)
  9:  '#00ffff',  // (0,  255, 255) – Cyan

  // Cyan → Magenta (red increases while green decreases; blue stays at 255)
  10: '#2ad4ff',  // (42, 212, 255)
  11: '#55aaff',  // (85, 170, 255)
  12: '#8080ff',  // (128,128,255)
  13: '#aa55ff',  // (170,85, 255)
  14: '#d42bff',  // (212,43, 255)
  15: '#ff00ff',  // (255,0,  255) – Magenta

  // Magenta → Red (blue decreases from 255 to 0; red stays 255)
  16: '#ff00e5',  // (255,0,229)
  17: '#ff00cc',  // (255,0,204)
  18: '#ff00b2',  // (255,0,178)
  19: '#ff0099',  // (255,0,153)
  20: '#ff007f',  // (255,0,127)
  21: '#ff0066',  // (255,0,102)
  22: '#ff00b2',  // (255,0,178)
  23: '#ff00ff',  // (255,0,255)
  24: '#d42bff',  // (212,43, 255)
  25: '#8080ff',  // (128,128,255)

  // Selected vertebra
  selected: '#ffd700', // Gold
  
  // Default for unknown labels
  default: '#808080', // Medium gray
};

// Convert a hex color to RGB array
export const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Handle 3-digit hex
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Convert to RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return [r, g, b];
};

// Convert a hex color to normalized RGB values for WebGL (0-1 range)
export const hexToNormalizedRgb = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return [r/255, g/255, b/255];
};

// Convert a hex color string to WebGL vec4 format with alpha
export const hexToVec4 = (hex, alpha = 0.7) => {
  const [r, g, b] = hexToNormalizedRgb(hex);
  return [r, g, b, alpha];
};

// Lookup vertebra color by label or number and return in specified format
export const getVertebraColor = (vertebraLabel, format = 'hex', isSelected = false) => {
  // If selected, return the gold color
  if (isSelected) {
    if (format === 'hex') return vertebraColors.selected;
    if (format === 'rgb') return hexToRgb(vertebraColors.selected);
    if (format === 'normalizedRgb') return hexToNormalizedRgb(vertebraColors.selected);
    if (format === 'vec4') return hexToVec4(vertebraColors.selected, 0.85); // Higher alpha for selected
  }
  
  // Determine if this is a label (like 'T1') or a number (like 8)
  let colorKey;
  if (typeof vertebraLabel === 'string') {
    // Map from label to number using standard mapping
    const vertebraToLabelMap = {
      'C1': 1, 'C2': 2, 'C3': 3, 'C4': 4, 'C5': 5, 'C6': 6, 'C7': 7,
      'T1': 8, 'T2': 9, 'T3': 10, 'T4': 11, 'T5': 12, 'T6': 13, 'T7': 14,
      'T8': 15, 'T9': 16, 'T10': 17, 'T11': 18, 'T12': 19,
      'L1': 20, 'L2': 21, 'L3': 22, 'L4': 23, 'L5': 24, 'S1': 25
    };
    colorKey = vertebraToLabelMap[vertebraLabel] || 0;
  } else {
    colorKey = vertebraLabel;
  }
  
  // Get the hex color
  const hexColor = vertebraColors[colorKey] || vertebraColors.default;
  
  // Return in requested format
  if (format === 'hex') return hexColor;
  if (format === 'rgb') return hexToRgb(hexColor);
  if (format === 'normalizedRgb') return hexToNormalizedRgb(hexColor);
  if (format === 'vec4') return hexToVec4(hexColor, 0.7);
  
  // Default fallback
  return hexColor;
};

// Map for reverse lookup - number to vertebra label
export const labelToVertebraMap = {
  1: 'C1', 2: 'C2', 3: 'C3', 4: 'C4', 5: 'C5', 6: 'C6', 7: 'C7',
  8: 'T1', 9: 'T2', 10: 'T3', 11: 'T4', 12: 'T5', 13: 'T6', 14: 'T7',
  15: 'T8', 16: 'T9', 17: 'T10', 18: 'T11', 19: 'T12',
  20: 'L1', 21: 'L2', 22: 'L3', 23: 'L4', 24: 'L5', 25: 'S1'
};

// Map for vertebra label to number
export const vertebraToLabelMap = {
  'C1': 1, 'C2': 2, 'C3': 3, 'C4': 4, 'C5': 5, 'C6': 6, 'C7': 7,
  'T1': 8, 'T2': 9, 'T3': 10, 'T4': 11, 'T5': 12, 'T6': 13, 'T7': 14,
  'T8': 15, 'T9': 16, 'T10': 17, 'T11': 18, 'T12': 19,
  'L1': 20, 'L2': 21, 'L3': 22, 'L4': 23, 'L5': 24, 'S1': 25
};